import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import FileTree from "./FileTree";
import FileViewer from "./FileViewer";
import { builderSelectors } from "../../../store/builderReducer";
import {
  loadSourcesTree,
  expandSourcesFolder,
  searchSourcesFiles,
  clearSourcesSearch,
  openSourcesFile,
  closeSourcesFile,
  activateSourcesTab,
  loadSourcesFileContent,
  changeSourcesFileContent,
  saveSourcesFile,
  toggleSourcesPath,
  expandSourcesPaths,
  getParentPaths,
  resolveFileConflictKeepMine,
  resolveFileConflictReload,
} from "../../../store/builderActionCreators";

import "./SourcesViewer.scss";

const SourcesViewer = ({ projectUid }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  // Selectors
  const tree = useSelector(builderSelectors.getSourcesTree);
  const treeLoading = useSelector(builderSelectors.isSourcesTreeLoading);
  const searchQuery = useSelector(builderSelectors.getSourcesSearchQuery);
  const searchResults = useSelector(builderSelectors.getSourcesSearchResults);
  const searchLoading = useSelector(builderSelectors.isSourcesSearchLoading);
  const openTabs = useSelector(builderSelectors.getSourcesOpenTabs);
  const activeTabPath = useSelector(builderSelectors.getSourcesActiveTabPath);
  const activeTab = useSelector(builderSelectors.getSourcesActiveTab);
  const expandedPaths = useSelector(builderSelectors.getSourcesExpandedPaths);
  const conflictTabs = useSelector(builderSelectors.getSourcesConflictTabs);

  // Debug logging for auto-expand troubleshooting
  useEffect(() => {
    console.log("[SourcesViewer] State changed:", {
      projectUid,
      treeExists: !!tree,
      treeLength: tree?.length,
      treeLoading,
      openTabsCount: openTabs.length,
      openTabPaths: openTabs.map(t => t.path),
      expandedPaths,
    });
  }, [projectUid, tree, treeLoading, openTabs, expandedPaths]);

  // Helper to find a node in the tree by path
  const findNodeByPath = useCallback((nodes, path) => {
    if (!nodes) return null;
    for (const node of nodes) {
      if (node.path === path) return node;
      if (node.children && path.startsWith(node.path + "/")) {
        const found = findNodeByPath(node.children, path);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Auto-expand parent folders for open tabs when tree loads
  // Also ensures children are loaded for expanded folders
  useEffect(() => {
    console.log("[SourcesViewer] Auto-expand effect running:", {
      treeExists: !!tree,
      treeLength: tree?.length,
      treeLoading,
      openTabsCount: openTabs.length,
    });

    // Only run when tree is loaded and we have open tabs
    if (tree && tree.length > 0 && openTabs.length > 0 && !treeLoading) {
      const allParentPaths = new Set();
      openTabs.forEach((tab) => {
        getParentPaths(tab.path).forEach((p) => allParentPaths.add(p));
      });
      console.log("[SourcesViewer] Parent paths to expand:", Array.from(allParentPaths));
      if (allParentPaths.size > 0) {
        dispatch(expandSourcesPaths(projectUid, Array.from(allParentPaths)));
      }
    }
  }, [tree, treeLoading, openTabs, dispatch, projectUid]); // Re-run when tree loads or tabs change

  // Re-load children for expanded folders that don't have children loaded
  // This handles the case where tree was re-fetched and lost the children
  useEffect(() => {
    if (!tree || !expandedPaths || treeLoading || !workspaceId || !projectUid) return;

    // Find expanded folders that don't have children loaded
    const foldersNeedingChildren = [];
    for (const path of expandedPaths) {
      const node = findNodeByPath(tree, path);
      console.log("[SourcesViewer] Checking expanded path:", path, {
        nodeFound: !!node,
        hasChildren: node?.hasChildren,
        childrenLoaded: !!node?.children,
      });
      if (node && node.hasChildren && !node.children) {
        foldersNeedingChildren.push(path);
      }
    }

    // Load children for folders that need it
    if (foldersNeedingChildren.length > 0) {
      console.log("[SourcesViewer] Loading children for folders:", foldersNeedingChildren);
      foldersNeedingChildren.forEach((path) => {
        dispatch(expandSourcesFolder(workspaceId, projectUid, path));
      });
    }
  }, [tree, expandedPaths, treeLoading, workspaceId, projectUid, dispatch, findNodeByPath]);

  // Load file tree on mount - BUT only if not already loaded
  // This preserves the tree with already-loaded children when switching tabs
  useEffect(() => {
    console.log("[SourcesViewer] Load tree effect:", {
      workspaceId,
      projectUid,
      treeExists: !!tree,
      willLoad: workspaceId && projectUid && !tree,
    });

    if (workspaceId && projectUid && !tree) {
      dispatch(loadSourcesTree(workspaceId, projectUid));
    }
    // No cleanup needed - clearBuilderChat() handles clearing on project change
  }, [dispatch, workspaceId, projectUid, tree]);

  // Handle folder expand (load children from backend)
  const handleFolderExpand = useCallback(
    (path) => {
      if (workspaceId && projectUid) {
        dispatch(expandSourcesFolder(workspaceId, projectUid, path));
      }
    },
    [dispatch, workspaceId, projectUid]
  );

  // Handle toggle expand (UI state)
  const handleToggleExpand = useCallback(
    (path) => {
      dispatch(toggleSourcesPath(projectUid, path));
    },
    [dispatch, projectUid]
  );

  // Handle search
  const handleSearch = useCallback(
    (query) => {
      if (workspaceId && projectUid) {
        dispatch(searchSourcesFiles(workspaceId, projectUid, query));
      }
    },
    [dispatch, workspaceId, projectUid]
  );

  // Handle search clear
  const handleSearchClear = useCallback(() => {
    dispatch(clearSourcesSearch(projectUid));
  }, [dispatch, projectUid]);

  // Handle file select (open in tab)
  const handleFileSelect = useCallback(
    (file) => {
      dispatch(openSourcesFile(projectUid, file));
      // Load content if not already loaded
      if (workspaceId && projectUid) {
        dispatch(loadSourcesFileContent(workspaceId, projectUid, file.path));
      }
    },
    [dispatch, workspaceId, projectUid]
  );

  // Handle tab close
  const handleTabClose = useCallback(
    (path) => {
      dispatch(closeSourcesFile(projectUid, path));
    },
    [dispatch, projectUid]
  );

  // Handle tab activate
  const handleTabActivate = useCallback(
    (path) => {
      dispatch(activateSourcesTab(projectUid, path));
      // Load content if not already loaded
      const tab = openTabs.find((t) => t.path === path);
      if (tab && tab.content === null && workspaceId && projectUid) {
        dispatch(loadSourcesFileContent(workspaceId, projectUid, path));
      }
    },
    [dispatch, openTabs, workspaceId, projectUid]
  );

  // Handle content change
  const handleContentChange = useCallback(
    (path, content) => {
      dispatch(changeSourcesFileContent(projectUid, path, content));
    },
    [dispatch, projectUid]
  );

  // Handle save
  const handleSave = useCallback(
    (path, content) => {
      if (workspaceId && projectUid) {
        dispatch(saveSourcesFile(intl, workspaceId, projectUid, path, content));
      }
    },
    [dispatch, intl, workspaceId, projectUid]
  );

  // Handle conflict resolution - keep my changes
  const handleConflictKeepMine = useCallback(
    (path) => {
      dispatch(resolveFileConflictKeepMine(projectUid, path));
    },
    [dispatch, projectUid]
  );

  // Handle conflict resolution - discard and reload
  const handleConflictReload = useCallback(
    (path) => {
      dispatch(resolveFileConflictReload(projectUid, path));
    },
    [dispatch, projectUid]
  );

  return (
    <div className="sources_viewer">
      <div className="file_tree_pane">
        <FileTree
          tree={tree}
          loading={treeLoading}
          searchQuery={searchQuery}
          searchResults={searchResults}
          searchLoading={searchLoading}
          onFolderExpand={handleFolderExpand}
          onSearch={handleSearch}
          onSearchClear={handleSearchClear}
          onFileSelect={handleFileSelect}
          activeFilePath={activeTabPath}
          expandedPaths={expandedPaths}
          onToggleExpand={handleToggleExpand}
        />
      </div>
      <div className="file_viewer_pane">
        <FileViewer
          tabs={openTabs}
          activeTabPath={activeTabPath}
          activeTab={activeTab}
          onTabClose={handleTabClose}
          onTabActivate={handleTabActivate}
          onContentChange={handleContentChange}
          onSave={handleSave}
          workspaceId={workspaceId}
          projectUid={projectUid}
          intl={intl}
          conflictTabs={conflictTabs}
          onConflictKeepMine={handleConflictKeepMine}
          onConflictReload={handleConflictReload}
        />
      </div>
    </div>
  );
};

export default SourcesViewer;
